import { Link } from "react-router-dom";
import Seo from "@/lib/seo/Seo";
import { getSeoForPath } from "@/lib/seo/routes.mjs";
import {
  ArrowRight,
  ChevronRight,
  Check,
  Rocket,
  ShieldCheck,
  Layers,
  KeyRound,
} from "lucide-react";
import CTASection from "@/components/CTASection";
import Reveal from "@/components/Reveal";
import ParticleField from "@/components/ParticleField";
import {
  GrowthIllustration,
  VirtualTerminalIllustration,
  OnlinePortalIllustration,
  ApiSdkIllustration,
  CheckoutIntegrationIllustration,
  ShoppingCartIllustration,
} from "@/components/illustrations/FintechIllustrations";

const VIRTUAL_TERMINAL_BULLETS = [
  "API integrations for seamless connectivity",
  "Card numbers tokenized and securely stored for future transactions",
  "Cash discount and surcharging options available for eligible merchants",
  "Convenient mobile app access for flexible payment management",
  "Email and SMS invoicing capabilities",
  "E-commerce integrations with multiple shopping carts",
  "Level 2 and Level 3 payment options",
  "Recurring payments setup is supported",
];

const ONLINE_PORTAL_BULLETS = [
  "Perform sales, voids, refunds, and tip adjustments from anywhere",
  "Real-time risk monitoring tools",
  "Fast boarding and deployment",
  "Customer engagement tools for communication and marketing",
  "Automatically build a customer database",
  "Advertise with digital and paper receipts",
];

const FEATURE_RECTANGLES = [
  {
    icon: Rocket,
    title: "Implementation",
    text: "Easy to implement and compatible with modern browser-based payment experiences.",
  },
  {
    icon: Layers,
    title: "Compatibility",
    text: "Designed to work with modern frontend frameworks, websites, and digital checkout environments.",
  },
  {
    icon: ShieldCheck,
    title: "Simple and Secure",
    text: "A simple, secure, and compliant way to collect sensitive cardholder data.",
  },
  {
    icon: KeyRound,
    title: "Best Authentication",
    text: "Strong authentication tools designed to support safer online transactions.",
  },
];

function BulletList({ items, testid }) {
  return (
    <ul className="mt-7 grid gap-3" data-testid={testid}>
      {items.map((b) => (
        <li key={b} className="flex items-start gap-3 text-[15px] text-slate-700 leading-[1.6]">
          <span className="mt-1 w-5 h-5 rounded-full bg-[var(--swipe-accent)]/12 text-[var(--swipe-accent)] flex items-center justify-center shrink-0">
            <Check className="w-3.5 h-3.5" strokeWidth={3.2} />
          </span>
          <span>{b}</span>
        </li>
      ))}
    </ul>
  );
}

function SectionLabel({ children }) {
  return (
    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[var(--swipe-accent-soft)] border border-[var(--swipe-accent)]/20 text-[11px] uppercase tracking-[0.22em] font-bold text-[var(--swipe-accent)] mb-5">
      <span className="w-1.5 h-1.5 rounded-full bg-[var(--swipe-accent)]" />
      {children}
    </div>
  );
}

function KnowMoreButton({ testid }) {
  return (
    <Link
      to="/contact"
      className="mt-9 inline-flex items-center gap-2 btn-primary-swipe text-[14.5px] py-3.5 px-7"
      data-testid={testid}
    >
      Know More <ArrowRight className="w-4 h-4" />
    </Link>
  );
}

export default function Ecommerce() {
  return (
    <div data-testid="ecommerce-page">
      <Seo {...getSeoForPath("/e-commerce")} />
      {/* ───────────────────────── HERO (unchanged) ───────────────────────── */}
      <section className="relative bg-[var(--swipe-navy)] text-white overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 grid-faint opacity-30 pointer-events-none" />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 55% 65% at 78% 50%, rgba(0,114,255,0.30) 0%, transparent 65%)" }} />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 40% 50% at 20% 30%, rgba(0,114,255,0.14) 0%, transparent 60%)" }} />
        <div aria-hidden="true" className="absolute -top-24 -left-20 w-[420px] h-[420px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.18)", filter: "blur(110px)" }} />
        <div aria-hidden="true" className="absolute -bottom-32 right-[12%] w-[480px] h-[480px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.16)", filter: "blur(120px)" }} />
        <div aria-hidden="true" className="absolute top-[30%] right-[2%] w-2 h-2 rounded-full bg-[var(--swipe-accent)] opacity-70 pointer-events-none" />
        <div aria-hidden="true" className="absolute top-[18%] left-[40%] w-1.5 h-1.5 rounded-full bg-[#7DB6FF] opacity-60 pointer-events-none" />
        <div aria-hidden="true" className="absolute bottom-[20%] left-[8%] w-1.5 h-1.5 rounded-full bg-[#7DB6FF] opacity-50 pointer-events-none" />

        <ParticleField density={36} connectDistance={130} className="absolute inset-0 w-full h-full" />

        <div className="container-swipe relative pt-14 pb-16 lg:pt-20 lg:pb-24">
          <nav className="flex items-center gap-2 text-[13px] text-white/50 mb-8">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/products" className="hover:text-white">Products</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white/80">E-Commerce</span>
          </nav>

          <div className="grid lg:grid-cols-[1.05fr,1fr] gap-10 lg:gap-12 items-center">
            <Reveal>
              <div className="relative">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/8 border border-white/15 text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#7DB6FF] mb-6">
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--swipe-accent)] animate-pulse" />
                  Online Payments
                </div>
                <h1 className="font-display-tight text-[2.75rem] sm:text-[3.75rem] lg:text-[5rem] xl:text-[5.5rem] font-extrabold leading-[1.02] text-white uppercase tracking-[-0.035em]">
                  ONLINE PAYMENTS &amp; <span className="text-[var(--swipe-accent)]"><span className="whitespace-nowrap">E-COMMERCE</span> SOLUTIONS</span>
                </h1>
                <p className="mt-7 text-[17px] md:text-[18px] text-white/75 leading-[1.7] max-w-xl">
                  Secure online payment processing with payment gateways, virtual terminals,
                  and API integrations for businesses selling online.
                </p>
                <div className="mt-9 flex flex-wrap gap-3">
                  <Link to="/savings-analysis" className="btn-primary-swipe text-[15px] py-3.5 px-7" data-testid="ecommerce-hero-cta-savings">
                    Free Savings Analysis <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link to="/contact" className="btn-outline-light text-[15px] py-3.5 px-7" data-testid="ecommerce-hero-cta-contact">
                    Talk to a Specialist
                  </Link>
                </div>
              </div>
            </Reveal>

            <Reveal variant="slide-right" delay={120}>
              <div className="relative">
                <GrowthIllustration transparent className="relative w-full h-auto max-w-[560px] mx-auto" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ───────────────── 1) CENTERED INTRO ───────────────── */}
      <section className="relative bg-white overflow-hidden">
        {/* subtle decorative blur */}
        <div aria-hidden="true" className="absolute -top-32 left-1/2 -translate-x-1/2 w-[640px] h-[640px] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(0,114,255,0.08) 0%, transparent 65%)" }} />

        <div className="container-swipe relative py-20 lg:py-28">
          <Reveal>
            <div className="max-w-[920px] mx-auto text-center">
              <div className="inline-flex items-center gap-2 mb-6">
                <span className="h-px w-10 bg-[var(--swipe-accent)]/40" />
                <span className="text-[11px] uppercase tracking-[0.24em] font-bold text-[var(--swipe-accent)]">
                  E-Commerce Payment Processing
                </span>
                <span className="h-px w-10 bg-[var(--swipe-accent)]/40" />
              </div>
              <h2
                data-testid="ecommerce-intro-title"
                className="font-display-tight text-[2rem] sm:text-[2.75rem] lg:text-[3.5rem] font-extrabold leading-[1.08] text-[var(--swipe-navy)] tracking-[-0.025em]"
              >
                <span className="relative inline-block">
                  <span className="relative z-10 text-[var(--swipe-accent)]">E-Commerce</span>
                  <span aria-hidden="true" className="absolute left-0 right-0 bottom-1 h-3 bg-[var(--swipe-accent-soft)] -z-0 rounded-sm" />
                </span>{" "}
                Payment Processing Solutions for Modern Businesses
              </h2>
            </div>
          </Reveal>
          <Reveal delay={140}>
            <p className="mt-7 max-w-[900px] mx-auto text-center text-[16.5px] md:text-[17.5px] text-slate-600 leading-[1.8]">
              Connect with customers through streamlined online payment solutions wherever they are.
              With Swipetronics{" "}
              <Link
                to="/services"
                className="text-[var(--swipe-accent)] underline underline-offset-2 hover:text-[#005FE0]"
                data-testid="ecommerce-link-services"
              >
                merchant services
              </Link>
              , businesses can accept debit cards, credit cards, ACH and bank transfers,
              SMS and Pay Now links, QR codes, and digital payments online through websites,
              iPhone apps, Android apps, and custom checkout experiences.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ───────────────── 2) STREAMLINING PAYMENTS · Virtual Terminal ───────────────── */}
      <section className="relative bg-[var(--swipe-muted)] overflow-hidden border-t border-line">
        <div aria-hidden="true" className="absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.08)", filter: "blur(110px)" }} />
        <div aria-hidden="true" className="absolute bottom-0 right-0 w-[360px] h-[360px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.06)", filter: "blur(100px)" }} />

        <div className="container-swipe relative py-20 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <Reveal>
              <div data-testid="ecommerce-virtual-terminal-text">
                <SectionLabel>Streamlining Payments</SectionLabel>
                <h2 className="font-display-tight text-[1.85rem] sm:text-[2.5rem] lg:text-[3rem] font-extrabold leading-[1.1] text-[var(--swipe-navy)] tracking-[-0.02em]">
                  Virtual Terminal
                </h2>
                <p className="mt-6 text-[16.5px] text-slate-600 leading-[1.8] max-w-xl">
                  An all-in-one virtual terminal, gateway, and mobile payment solution designed to
                  help businesses accept payments online, over the phone, by invoice, or from a
                  browser.
                </p>
                <BulletList items={VIRTUAL_TERMINAL_BULLETS} testid="ecommerce-virtual-terminal-bullets" />
                <KnowMoreButton testid="ecommerce-virtual-terminal-cta" />
              </div>
            </Reveal>

            <Reveal variant="slide-right" delay={120}>
              <div className="relative">
                <VirtualTerminalIllustration className="relative w-full h-auto max-w-[600px] mx-auto" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ───────────────── 3) ONE ECOSYSTEM · Online Portal ───────────────── */}
      <section className="relative bg-white overflow-hidden">
        <div aria-hidden="true" className="absolute top-0 left-0 w-[360px] h-[360px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.06)", filter: "blur(100px)" }} />
        <div aria-hidden="true" className="absolute -bottom-32 -right-32 w-[420px] h-[420px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.08)", filter: "blur(110px)" }} />

        <div className="container-swipe relative py-20 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <Reveal variant="slide-left" delay={120} className="lg:order-1 order-2">
              <div className="relative">
                <OnlinePortalIllustration className="relative w-full h-auto max-w-[600px] mx-auto" />
              </div>
            </Reveal>

            <Reveal className="lg:order-2 order-1">
              <div data-testid="ecommerce-online-portal-text">
                <SectionLabel>One Ecosystem</SectionLabel>
                <h2 className="font-display-tight text-[1.85rem] sm:text-[2.5rem] lg:text-[3rem] font-extrabold leading-[1.1] text-[var(--swipe-navy)] tracking-[-0.02em]">
                  Online Portal
                </h2>
                <p className="mt-6 text-[16.5px] text-slate-600 leading-[1.8] max-w-xl">
                  Our online portal gives merchants convenient access to transaction data, customer
                  information, and payment activity. This helps business owners stay informed,
                  review activity, and make better decisions anytime.
                </p>
                <BulletList items={ONLINE_PORTAL_BULLETS} testid="ecommerce-online-portal-bullets" />
                <KnowMoreButton testid="ecommerce-online-portal-cta" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ───────────────── 4) A SEAMLESS EXPERIENCE · APIs and SDKs ───────────────── */}
      <section className="relative bg-[var(--swipe-navy)] text-white overflow-hidden">
        <div className="absolute inset-0 grid-faint opacity-30 pointer-events-none" />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 55% 65% at 80% 50%, rgba(0,114,255,0.22) 0%, transparent 65%)" }} />
        <div aria-hidden="true" className="absolute -top-32 left-[10%] w-[420px] h-[420px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.16)", filter: "blur(110px)" }} />

        <div className="container-swipe relative py-20 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <Reveal>
              <div data-testid="ecommerce-api-sdk-text">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/8 border border-white/15 text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#7DB6FF] mb-5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--swipe-accent)]" />
                  A Seamless Experience
                </div>
                <h2
                  className="font-display-tight text-[1.85rem] sm:text-[2.5rem] lg:text-[2.85rem] font-extrabold leading-[1.1] tracking-[-0.02em]"
                  style={{ color: "#FFFFFF" }}
                >
                  Reliable, Straightforward{" "}
                  <span className="text-[var(--swipe-accent)]">APIs and SDKs</span>
                </h2>
                <p className="mt-6 text-[16.5px] leading-[1.85] max-w-xl" style={{ color: "rgba(255,255,255,0.75)" }}>
                  A key factor in selecting an online payment solution is how easy it is to set up
                  and integrate. With APIs and SDKs, developers can connect payment technology into
                  websites, apps, and digital platforms using reliable pre-built tools.
                </p>
                <p className="mt-5 text-[16.5px] leading-[1.85] max-w-xl" style={{ color: "rgba(255,255,255,0.75)" }}>
                  Swipetronics helps businesses access online payment solutions with clear setup
                  guidance, flexible integration options, and a smoother path to accepting payments
                  online.
                </p>
                <KnowMoreButton testid="ecommerce-api-sdk-cta" />
              </div>
            </Reveal>

            <Reveal variant="slide-right" delay={120}>
              <div className="relative">
                <ApiSdkIllustration className="relative w-full h-auto max-w-[600px] mx-auto" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ───────────────── 5) VERTICAL FEATURE RECTANGLES ───────────────── */}
      <section className="relative bg-white overflow-hidden border-t border-line">
        <div aria-hidden="true" className="absolute -bottom-32 left-1/2 -translate-x-1/2 w-[640px] h-[640px] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(0,114,255,0.06) 0%, transparent 65%)" }} />

        <div className="container-swipe relative py-20 lg:py-24">
          <Reveal>
            <div className="max-w-[900px] mx-auto text-center mb-14">
              <SectionLabel>Built for Online</SectionLabel>
              <h2 className="font-display-tight text-[1.85rem] sm:text-[2.5rem] font-extrabold leading-[1.12] text-[var(--swipe-navy)] tracking-[-0.02em]">
                Designed for modern online payment experiences.
              </h2>
              <p className="mt-5 text-[16px] text-slate-600 leading-[1.75] max-w-[760px] mx-auto">
                A practical foundation for accepting payments online &mdash; built around clean
                integration, secure handling, and modern checkout standards.
              </p>
            </div>
          </Reveal>

          <div className="grid lg:grid-cols-[1fr,1fr] gap-10 lg:gap-14 items-center">
            <Reveal variant="slide-left">
              <div className="relative">
                <CheckoutIntegrationIllustration className="relative w-full h-auto max-w-[600px] mx-auto" />
              </div>
            </Reveal>

            <Reveal variant="slide-right" delay={120}>
              <div className="grid gap-5">
                {FEATURE_RECTANGLES.map((card, i) => {
                  const Icon = card.icon;
                  const isAccent = i % 2 === 1;
                  return (
                    <div
                      key={card.title}
                      data-testid={`ecommerce-feature-rect-${i}`}
                      className={`group relative flex items-start gap-5 rounded-2xl p-6 lg:p-7 border transition-all duration-300 hover:-translate-y-1 hover:border-[var(--swipe-accent)] cursor-default ${
                        isAccent
                          ? "bg-[var(--swipe-navy)] border-transparent shadow-[0_18px_40px_-24px_rgba(6,26,58,0.55)] hover:shadow-[0_28px_56px_-22px_rgba(0,114,255,0.55)]"
                          : "bg-[var(--swipe-accent-soft)] border-[var(--swipe-accent)]/15 shadow-[0_10px_30px_-22px_rgba(6,26,58,0.20)] hover:shadow-[0_24px_50px_-22px_rgba(0,114,255,0.35)]"
                      }`}
                    >
                      <div
                        className={`w-14 h-14 shrink-0 rounded-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-3 ${
                          isAccent
                            ? "bg-[var(--swipe-accent)] shadow-[0_14px_28px_-10px_rgba(0,114,255,0.55)]"
                            : "bg-white shadow-[0_10px_22px_-12px_rgba(0,114,255,0.35)]"
                        }`}
                      >
                        <Icon
                          className={`w-7 h-7 ${isAccent ? "text-white" : "text-[var(--swipe-accent)]"}`}
                          strokeWidth={2.2}
                        />
                      </div>
                      <div>
                        <h3
                          className="font-display-tight text-[19px] font-extrabold leading-tight tracking-[-0.01em]"
                          style={{ color: isAccent ? "#FFFFFF" : "var(--swipe-navy)" }}
                        >
                          {card.title}
                        </h3>
                        <p
                          className="mt-2 text-[14.5px] leading-[1.7]"
                          style={{ color: isAccent ? "rgba(255,255,255,0.78)" : "rgb(71, 85, 105)" }}
                        >
                          {card.text}
                        </p>
                      </div>
                      {/* hover top accent line */}
                      <span
                        aria-hidden="true"
                        className="absolute top-0 left-6 right-6 h-[2px] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500 bg-[var(--swipe-accent)] rounded-full"
                      />
                    </div>
                  );
                })}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ───────────────── 6) FINAL · Shopping Cart Integrations ───────────────── */}
      <section className="relative bg-gradient-to-b from-[var(--swipe-muted)] to-white overflow-hidden border-t border-line">
        <div aria-hidden="true" className="absolute -top-32 right-[10%] w-[440px] h-[440px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.08)", filter: "blur(110px)" }} />
        <div aria-hidden="true" className="absolute -bottom-32 left-[8%] w-[420px] h-[420px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.07)", filter: "blur(110px)" }} />

        <div className="container-swipe relative py-20 lg:py-28">
          <div className="grid lg:grid-cols-[1.05fr,1fr] gap-12 lg:gap-16 items-center">
            <Reveal>
              <div data-testid="ecommerce-cart-text">
                <SectionLabel>Connected Checkout</SectionLabel>
                <h2 className="font-display-tight text-[2rem] sm:text-[2.75rem] lg:text-[3.25rem] font-extrabold leading-[1.08] text-[var(--swipe-navy)] tracking-[-0.025em]">
                  Shopping Cart{" "}
                  <span className="text-[var(--swipe-accent)]">Integrations</span>
                </h2>
                <p className="mt-6 text-[17px] text-slate-600 leading-[1.8] max-w-xl">
                  Allow customers to add items from an online store to their cart and proceed
                  through checkout safely and securely.
                </p>

                <div className="mt-9 flex flex-wrap gap-3">
                  <Link
                    to="/get-started"
                    className="btn-primary-swipe text-[15px] py-3.5 px-7"
                    data-testid="ecommerce-cart-cta-savings"
                  >
                    Get Started <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link
                    to="/contact"
                    className="btn-outline-swipe text-[15px] py-3.5 px-7"
                    data-testid="ecommerce-cart-cta-contact"
                  >
                    Talk to a Specialist
                  </Link>
                </div>
              </div>
            </Reveal>

            <Reveal variant="slide-right" delay={120}>
              <div className="relative">
                <ShoppingCartIllustration className="relative w-full h-auto max-w-[600px] mx-auto" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <CTASection
        eyebrow="E-Commerce Payments"
        title="Ready to bring your storefront online?"
        description="Swipetronics can help you set up secure online checkout, hosted payment pages, and the integrations your store actually needs."
      />
    </div>
  );
}
